/**
 * The list standard on the client side (SDD-017 §10, L-13): build `filters` and `search` with
 * builders that escape what the grammar reserves, walk the pages of any list with `paginate`,
 * narrow a refused list request with `isListError`, and type a filter catalog. Nobody
 * concatenates a filter string by hand — `Smith, John` is `Smith\, John` on the wire, and a
 * hand-built string forgets it the first time a name has a comma.
 *
 * Shared by every service SDK (re-exported by `@eis/insuranceservice-sdk` and
 * `@eis/billingservice-sdk`); the grammar is the company's, the same `pkg/listquery` parses on
 * the server.
 */

/** A filter catalog field's type (SDD-017 §5.2). */
export type FieldType =
  'STRING' | 'NUMBER' | 'INTEGER' | 'MONEY' | 'DATE' | 'DATETIME' | 'BOOLEAN' | 'ENUM';

/** A `filters` operator, spelled as the grammar writes it. */
export type Operator =
  | '=='
  | '!='
  | '>'
  | '<'
  | '>='
  | '<='
  | '=@'
  | '!@'
  | '=~'
  | '!~'
  | '=()'
  | '!()'
  | '==$null'
  | '!=$null';

export const OPERATORS: readonly Operator[] = [
  '==',
  '!=',
  '>',
  '<',
  '>=',
  '<=',
  '=@',
  '!@',
  '=~',
  '!~',
  '=()',
  '!()',
  '==$null',
  '!=$null',
];

export interface LocalizedText {
  readonly de?: string;
  readonly en?: string;
}

export interface FilterValue {
  readonly value?: string;
  /** A system enum's translation key. */
  readonly labelKey?: string;
  /** A product enum's de/en label. */
  readonly label?: LocalizedText;
}

/** One catalog field, as `GET …:filterCatalog` answers it. */
export interface FilterField {
  readonly key?: string;
  /** `SYSTEM` or `PRODUCT`. */
  readonly group?: string;
  readonly labelKey?: string;
  readonly label?: LocalizedText;
  /** A {@link FieldType}. */
  readonly type?: string;
  /** {@link Operator}s. */
  readonly operators?: readonly string[];
  readonly values?: readonly FilterValue[];
  readonly sortable?: boolean;
  readonly searchable?: boolean;
  readonly pii?: boolean;
  readonly suggest?: boolean;
  readonly versions?: readonly string[];
  readonly identifier?: boolean;
  readonly pattern?: string;
  readonly min?: number;
  readonly max?: number;
}

/** A collection's filter catalog. */
export interface FilterCatalog {
  readonly fields?: readonly FilterField[];
}

/** Escapes the characters the grammar reserves inside a value: `;` `,` `|` and `\`. */
export function escapeFilterValue(value: string): string {
  return value.replace(/[;,|\\]/g, (c) => `\\${c}`);
}

type Clause = string;

/**
 * Builds a `filters` value: every method adds one AND-ed clause, `or()` adds one AND-ed group of
 * OR-ed clauses (OR binds tighter than AND in the grammar, so a group needs no parentheses).
 *
 *     filters().eq('status', 'ACTIVE').in('data.cover.coverage_tier', ['BASIC', 'COMFORT']).toString()
 *     // status==ACTIVE;data.cover.coverage_tier=(BASIC|COMFORT)
 */
export class FiltersBuilder {
  private readonly groups: Clause[][] = [];

  private add(clause: Clause): this {
    this.groups.push([clause]);
    return this;
  }

  private static clause(field: string, op: Operator, value?: string | readonly string[]): Clause {
    if (op === '==$null' || op === '!=$null') return `${field}${op}`;
    if (op === '=()' || op === '!()') {
      const values = (Array.isArray(value) ? value : [value ?? '']) as readonly string[];
      return `${field}${op === '=()' ? '=(' : '!('}${values.map(escapeFilterValue).join('|')})`;
    }
    return `${field}${op}${escapeFilterValue(String(value ?? ''))}`;
  }

  /** Any clause, by operator. */
  where(field: string, op: Operator, value?: string | readonly string[]): this {
    return this.add(FiltersBuilder.clause(field, op, value));
  }
  eq(field: string, value: string): this {
    return this.where(field, '==', value);
  }
  ne(field: string, value: string): this {
    return this.where(field, '!=', value);
  }
  gt(field: string, value: string | number): this {
    return this.where(field, '>', String(value));
  }
  lt(field: string, value: string | number): this {
    return this.where(field, '<', String(value));
  }
  ge(field: string, value: string | number): this {
    return this.where(field, '>=', String(value));
  }
  le(field: string, value: string | number): this {
    return this.where(field, '<=', String(value));
  }
  contains(field: string, value: string): this {
    return this.where(field, '=@', value);
  }
  excludes(field: string, value: string): this {
    return this.where(field, '!@', value);
  }
  matches(field: string, pattern: string): this {
    return this.where(field, '=~', pattern);
  }
  notMatches(field: string, pattern: string): this {
    return this.where(field, '!~', pattern);
  }
  in(field: string, values: readonly string[]): this {
    return this.where(field, '=()', values);
  }
  notIn(field: string, values: readonly string[]): this {
    return this.where(field, '!()', values);
  }
  isNull(field: string): this {
    return this.where(field, '==$null');
  }
  isNotNull(field: string): this {
    return this.where(field, '!=$null');
  }

  /** One AND-ed group whose clauses are OR-ed: `.or(f => f.eq('a','1').eq('a','2'))` → `a==1,a==2`. */
  or(build: (inner: FiltersBuilder) => FiltersBuilder): this {
    const inner = build(new FiltersBuilder());
    const clauses = inner.groups.flat();
    if (clauses.length > 0) this.groups.push(clauses);
    return this;
  }

  isEmpty(): boolean {
    return this.groups.length === 0;
  }

  toString(): string {
    return this.groups.map((group) => group.join(',')).join(';');
  }
}

export function filters(): FiltersBuilder {
  return new FiltersBuilder();
}

/**
 * Builds a `search` value (SDD-017 §8.4): free terms, `field:value` (a trailing `*` is a prefix),
 * and phrases. A term with whitespace becomes a quoted phrase.
 *
 *     search().term('müller').field('data.bike.frame_number', 'WX83*').toString()
 *     // müller data.bike.frame_number:WX83*
 */
export class SearchBuilder {
  private readonly parts: string[] = [];

  term(text: string): this {
    const trimmed = text.trim();
    if (trimmed !== '')
      this.parts.push(/\s/.test(trimmed) ? `"${trimmed.replace(/"/g, '')}"` : trimmed);
    return this;
  }
  phrase(text: string): this {
    const trimmed = text.trim().replace(/"/g, '');
    if (trimmed !== '') this.parts.push(`"${trimmed}"`);
    return this;
  }
  field(key: string, value: string): this {
    const trimmed = value.trim().replace(/\s+/g, '');
    if (trimmed !== '') this.parts.push(`${key}:${trimmed}`);
    return this;
  }
  isEmpty(): boolean {
    return this.parts.length === 0;
  }
  toString(): string {
    return this.parts.join(' ');
  }
}

export function search(): SearchBuilder {
  return new SearchBuilder();
}

/** The error codes of a refused list request (SDD-017 §5.3). */
export type ListErrorCode =
  | 'INVALID_FILTER'
  | 'INVALID_SEARCH'
  | 'INVALID_ORDER'
  | 'INVALID_PAGE_TOKEN'
  | 'INVALID_EXPAND'
  | 'INVALID_ARGUMENT';

export const LIST_ERROR_CODES: readonly ListErrorCode[] = [
  'INVALID_FILTER',
  'INVALID_SEARCH',
  'INVALID_ORDER',
  'INVALID_PAGE_TOKEN',
  'INVALID_EXPAND',
  'INVALID_ARGUMENT',
];

/** What a refused list request says: the code, and the field and reason of the violation. */
export interface ListErrorDetail {
  readonly code: ListErrorCode;
  readonly field?: string | undefined;
  readonly reason?: string | undefined;
  readonly message?: string | undefined;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Reads a list refusal out of an error body (`google.rpc.Status` JSON: an `ErrorInfo` detail
 * whose `reason` is the code, a `BadRequest` detail with the field violation). `undefined` when
 * the body is not one.
 */
export function listErrorOf(error: unknown): ListErrorDetail | undefined {
  if (!isRecord(error)) return undefined;
  const details = Array.isArray(error.details) ? (error.details as unknown[]) : [];
  let code: ListErrorCode | undefined;
  let field: string | undefined;
  let reason: string | undefined;
  let message: string | undefined;
  for (const detail of details) {
    if (!isRecord(detail)) continue;
    const candidate = detail.reason;
    if (
      typeof candidate === 'string' &&
      (LIST_ERROR_CODES as readonly string[]).includes(candidate)
    ) {
      code = candidate as ListErrorCode;
    }
    const violations = detail.fieldViolations;
    if (Array.isArray(violations) && isRecord(violations[0])) {
      const violation = violations[0];
      field = typeof violation.field === 'string' ? violation.field : field;
      reason = typeof violation.reason === 'string' ? violation.reason : reason;
      message = typeof violation.description === 'string' ? violation.description : message;
    }
  }
  if (code === undefined) return undefined;
  return {
    code,
    field,
    reason,
    message: message ?? (typeof error.message === 'string' ? error.message : undefined),
  };
}

/** Narrows an error to a list refusal, optionally of one code. */
export function isListError(error: unknown, code?: ListErrorCode): boolean {
  const detail = listErrorOf(error);
  return detail !== undefined && (code === undefined || detail.code === code);
}

/** A page of any standard list. */
export interface ListPage<Item> {
  readonly items?: readonly Item[];
  readonly nextPageToken?: string;
  readonly validAt?: string;
  readonly asOf?: string;
  readonly activeOn?: string;
}

/** The slice of an openapi-fetch client `paginate` needs. */
export interface ListGetter {
  GET: (
    path: never,
    init: never,
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
}

/** Thrown by `paginate` when a page is refused; carries the body for {@link listErrorOf}. */
export class ListPageError extends Error {
  override readonly name = 'ListPageError';
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown) {
    super(`list page refused with ${String(status)}`);
    this.status = status;
    this.body = body;
  }
}

/**
 * Walks every page of a list, yielding its items: follows `nextPageToken` until it is empty and
 * keeps the cut — page 1's echoed `validAt`/`asOf` are sent with every later page (the token pins
 * them too; sending them makes the intent explicit and a drift a 400, not a silent new cut).
 *
 *     for await (const row of paginate(instance, '/v1/policies', { query: { filters: f } })) …
 */
export async function* paginate<Item>(
  client: ListGetter,
  path: string,
  params: {
    readonly query?: Record<string, unknown>;
    readonly path?: Record<string, unknown>;
  } = {},
): AsyncGenerator<Item, void, undefined> {
  let pageToken: string | undefined;
  const cut: Record<string, string> = {};
  for (;;) {
    const query = { ...params.query, ...cut, ...(pageToken === undefined ? {} : { pageToken }) };
    const result = await client.GET(path as never, { params: { ...params, query } } as never);
    if (result.error !== undefined || result.data === undefined) {
      throw new ListPageError(result.response.status, result.error);
    }
    const page = result.data as ListPage<Item>;
    for (const item of page.items ?? []) yield item;
    if (!page.nextPageToken) return;
    pageToken = page.nextPageToken;
    if (Object.keys(cut).length === 0) {
      for (const key of ['validAt', 'asOf', 'activeOn'] as const) {
        const value = page[key];
        if (typeof value === 'string' && value !== '') cut[key] = value;
      }
    }
  }
}
